import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { useCallback, useMemo, useEffect, useRef } from 'react'
import { apiClient } from '@/api/client'
import type { DeepOrder } from '../types'
import { sortOrders, countOrdersByStatus } from '../utils'
import { fetchPosPayments } from '@/features/pos-ordering/service'

export function useOrders(restaurantId: number, date: string, posEnabled = false) {
  const queryClient = useQueryClient()
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const ordersQuery = useQuery({
    queryKey: ['orders', restaurantId, date, posEnabled],
    queryFn: async () => {
      const response = await apiClient.get<DeepOrder[]>(
        `/api/admin/restaurants/${restaurantId}/orders?date=${date}`
      )
      if (!posEnabled) return response.data
      const posOrders = response.data.filter(order => order.userId.startsWith('pos:'))
      if (posOrders.length === 0) return response.data
      try {
        const batches = []
        for (let i = 0; i < posOrders.length; i += 1000) batches.push(posOrders.slice(i, i + 1000).map(order => order.id))
        const payments = (await Promise.all(batches.map(ids => fetchPosPayments(restaurantId, ids)))).flat()
        const byId = new Map(payments.map(payment => [payment.orderId, payment]))
        return response.data.map(order => order.userId.startsWith('pos:')
          ? { ...order, posRestaurantId: restaurantId, posPayment: byId.get(order.id) ?? null } : order)
      } catch {
        // Keep order preparation available; a failed payment read must never imply unpaid.
        return response.data.map(order => order.userId.startsWith('pos:')
          ? { ...order, posRestaurantId: restaurantId, posPaymentUnavailable: true } : order)
      }
    },
    enabled: !!restaurantId && !!date,
    staleTime: 15000, // Consider data fresh for 15 seconds
    // Keep showing the previous day's list while navigating between dates
    // instead of flashing the full skeleton on every switch.
    placeholderData: keepPreviousData,
  })

  // Sort and process orders
  const orders = useMemo(() => {
    return sortOrders(ordersQuery.data ?? [])
  }, [ordersQuery.data])

  // Count orders by status for stats display
  const orderCounts = useMemo(() => {
    return countOrdersByStatus(orders)
  }, [orders])

  // Get a single order by ID
  const getOrderById = (orderId: number): DeepOrder | undefined => {
    return orders.find((order) => order.id === orderId)
  }

  // Start auto-refresh polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
  }, [])

  const startPolling = useCallback((intervalMs = 30000) => {
    stopPolling()
    pollingIntervalRef.current = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: ['orders', restaurantId, date] })
    }, intervalMs)
  }, [date, queryClient, restaurantId, stopPolling])

  // Cleanup on unmount
  useEffect(() => {
    return () => stopPolling()
  }, [stopPolling])

  // Manual refresh function
  const refresh = useCallback(() => {
    return queryClient.invalidateQueries({ queryKey: ['orders', restaurantId, date] })
  }, [date, queryClient, restaurantId])

  return {
    orders,
    orderCounts,
    isLoading: ordersQuery.isLoading,
    isFetching: ordersQuery.isFetching,
    error: ordersQuery.error,
    getOrderById,
    refresh,
    refetch: ordersQuery.refetch,
    startPolling,
    stopPolling,
  }
}
